import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Link, Box, CssBaseline, Typography, Button, Checkbox, Dialog, DialogTitle, DialogContent, TextField, CircularProgress } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { ThumbsDown, ThumbsUp, Minus } from "lucide-react";
import FingerprintJS from '@fingerprintjs/fingerprintjs';

const theme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#000000",
    },
    text: {
      primary: "#FFFFFF",
    },
  },
});

// Reusable PollItem component
function PollItem({ icon: Icon, label, isSelected, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
        cursor: "pointer",
        transform: isSelected ? "scale(1.1)" : "scale(1)",
        transition: "transform 0.2s ease",
      }}
    >
      <Box
        sx={{
          backgroundColor: isSelected ? "white" : "rgb(39, 39, 42)",
          borderRadius: "50%",
          width: "64px",
          height: "64px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: isSelected ? "black" : "white",
          textAlign: "center",
        }}
      >
        <Icon
          style={{
            fill: isSelected ? "black" : "none",
            color: isSelected ? "black" : "white",
            width: "32px",
            height: "32px",
          }}
        />
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: "white",
          fontWeight: "500",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// Reusable Section component
const Section = React.forwardRef(({ title, children, backgroundColor = "rgb(39, 39, 42)" }, ref) => {
  return (
    <Box
      ref={ref}
      sx={{
        backgroundColor: backgroundColor,
        marginTop: "32px",
        padding: "16px",
        borderRadius: "8px",
        textAlign: "left",
        maxWidth: "600px",
        width: "100%",
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontWeight: "bold",
          color: "white",
          marginBottom: "8px",
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
});

// Reusable ArgumentList component
function ArgumentList({ argumentsList, selectedArguments, setSelectedArguments }) {
  const handleCheckboxChange = (argument) => {
    if (selectedArguments.includes(argument)) {
      setSelectedArguments(selectedArguments.filter((item) => item !== argument));
    } else {
      setSelectedArguments([...selectedArguments, argument]);
    }
  };

  return argumentsList.map((argument, index) => (
    <Box
      key={index}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "8px",
      }}
    >
      <Checkbox
        checked={selectedArguments.includes(argument)}
        onChange={() => handleCheckboxChange(argument)}
        sx={{
          color: "rgb(161, 161, 170)",
          "&.Mui-checked": {
            color: "white",
          },
        }}
      />
      <Typography variant="body2" sx={{ color: "rgb(161, 161, 170)" }}>
        {argument}
      </Typography>
    </Box>
  ));
}

function PollPublicPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams(); // Get search parameters
  const [selectedPollItem, setSelectedPollItem] = useState(null);
  const [selectedProponentArguments, setSelectedProponentArguments] = useState([]);
  const [selectedOpponentArguments, setSelectedOpponentArguments] = useState([]);
  const [pollData, setPollData] = useState(null); // State to store API data
  const [ipData, setIpData] = useState(null); // State to store IP data
  const [dialogOpen, setDialogOpen] = useState(false); // State to control dialog visibility
  const [zipCode, setZipCode] = useState(""); // State to store entered ZIP code
  const [visitorId, setVisitorId] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [script, setScript] = useState(""); // State to store the call script
  const [candidates, setCandidates] = useState([]); // State to store candidates
  const repsRef = useRef(null);
  const [pollLogId, setPollLogId] = useState(null); // State to store poll log ID
  const [repClicks, setRepClicks] = useState([]);

  useEffect(() => {
      FingerprintJS.load().then(fp => {
        fp.get().then(result => {
          setVisitorId(result.visitorId);
        });
      });
  }, []);
    
  useEffect(() => {
    // Fetch data from the API on page load
    fetch(`${process.env.REACT_APP_API_URL}/poll/${slug}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${process.env.REACT_APP_API_TOKEN}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setPollData(data); 
      })
      .catch((error) => {
        console.error("Error fetching poll data:", error);
      });
  }, []); // Empty dependency array ensures this runs only once on mount

  const handlePollItemClick = (item) => {
    setSelectedPollItem(item); // Update the state
  };
  
  useEffect(() => {
    if (selectedPollItem || selectedProponentArguments || selectedOpponentArguments) {
      savePollLog(); // Trigger savePollLog when any relevant state changes
    }
  }, [selectedPollItem, selectedProponentArguments, selectedOpponentArguments]); // Combine dependencies into a single effect

  const findMyRep = () => {
    if (ipData && ipData.country !== "US") {
      alert("This feature is currently only available for users in the United States.");
      return;
    }

    if (!selectedPollItem) {
      alert("Please select your vote before proceeding.");
      return;
    }

    if (selectedProponentArguments.length === 0 && selectedOpponentArguments.length === 0) {
      alert("Please select at least one proponent or opponent argument.");
      return;
    }

    if (searchParams.get("zip")) {
      setZipCode(searchParams.get("zip")); // Set ZIP code from search parameters
      setDialogLoading(true);
      savePollLog(true, searchParams.get("zip"));
    }

    // Open the dialog
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const scrollToReps = () => {
    setTimeout(() => {
      repsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };  

  const savePollLog = (createScript = false, zip = zipCode) => {
    // Make an API call to log the poll data

    setDialogLoading(true);

    const url = pollLogId 
      ? `${process.env.REACT_APP_API_URL}/poll-log/${pollLogId}/` 
      : `${process.env.REACT_APP_API_URL}/poll-log/`;

    const method = pollLogId ? "PUT" : "POST";

    fetch(url, {
      method: method,
      headers: {
      "Content-Type": "application/json",
      Authorization: `Token ${process.env.REACT_APP_API_TOKEN}`,
      },
      body: JSON.stringify({
      ip_address: ipData?.ip || null,
      zip_code: zip,
      fingerprint: visitorId,
      issue: pollData?.id,
      vote: selectedPollItem,
      proponent_arguments: selectedProponentArguments,
      opponent_arguments: selectedOpponentArguments,
      create_script: createScript, 
      call_my_rep_clicked: createScript,
      rep_clicks: repClicks
      }),
    })
      .then((response) => {
        setDialogLoading(false);

        if (!response.ok) {
          throw new Error("Failed to log poll data");
        }

        return response.json();
      })
      .then((data) => {
        setPollLogId(data.id); // Store the poll log ID

        console.log(data);

        if (data.error){
          alert(data.error);
          return;
        }

        if (createScript) {
          setScript(data.script); // Set the script from the response
          setCandidates(data.candidates || []); // Set the candidates from the response

          setDialogOpen(false);
          setTimeout(scrollToReps, 100);
        }
      })
      .catch((error) => {
        console.error("Error logging poll data:", error);
      });
  };

  const handleZipSubmit = () => {
    const zipCodeRegex = /^[0-9]{5}(?:-[0-9]{4})?$/;

    if (!zipCodeRegex.test(zipCode)) {
        alert("Please enter a valid US ZIP code.");
        return;
    }

    savePollLog(true); // Call the function to save the poll log with script generation
  };

  useEffect(() => {
    // Fetch IP data to find the representative
    fetch(`https://ipapi.co/json/`)
        .then((response) => {
            if (response.status === 200) {
                return response.json();
            } else if (response.status === 429) {
                setZipCode(""); // Set ZIP code to blank if rate-limited
                throw new Error("Rate limit exceeded");
            } else {
                throw new Error("Failed to fetch IP data");
            }
        })
        .then((data) => {
            setIpData(data);
            setZipCode(data.postal || ""); // Set the ZIP code from IP data
        })
        .catch((error) => {
            console.error("Error fetching user location data:", error);
        });
  }, []);

  const pollItems = [
    { icon: ThumbsDown, label: "Strongly Oppose", value: "strongly_oppose" },
    { icon: ThumbsDown, label: "Oppose", value: "oppose" },
    { icon: Minus, label: "Neutral", value: "neutral" },
    { icon: ThumbsUp, label: "Support", value: "support" },
    { icon: ThumbsUp, label: "Strongly Support", value: "strongly_support" },
  ];

return (
    <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
            sx={{
                backgroundColor: "background.default",
                minHeight: "100vh",
                color: "text.primary",
                padding: "24px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                overflowY: "auto",
            }}
        >
            <Typography
                variant="h4"
                component="h2"
                sx={{
                    fontWeight: "bold",
                    marginBottom: "24px",
                    maxWidth: "600px",
                    lineHeight: "1.5",
                }}
            >
                {pollData && pollData.statement ? pollData.statement : <CircularProgress />}
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "24px",
                    marginBottom: "16px",
                }}
            >
                {pollItems.map((item) => (
                    <PollItem
                        key={item.value}
                        icon={item.icon}
                        label={item.label}
                        isSelected={selectedPollItem === item.value}
                        onClick={() => handlePollItemClick(item.value)}
                    />
                ))}
            </Box>

            <Box
                sx={{
                    textAlign: "left",
                    maxWidth: "600px",
                    width: "100%",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.875rem",
                        color: "rgb(161, 161, 170)",
                        marginBottom: "32px",
                    }}
                >
                    Checking an agreement will personalize your call script.
                </Typography>
            </Box>

            <Button
                onClick={findMyRep} 
                sx={{
                    backgroundColor: "rgb(250, 250, 250)",
                    color: "black",
                    borderRadius: "4px",
                    padding: "12px 24px",
                    fontSize: "1rem",
                    fontWeight: "bold",
                    textTransform: "none",
                    width: "100%",
                    maxWidth: "600px",
                    "&:hover": {
                        backgroundColor: "rgb(229, 231, 235)",
                    },
                }}
            >
                Call My Rep
            </Button>

            <Section title="Context">
                <Typography
                    variant="body2"
                    sx={{
                        color: "rgb(161, 161, 170)",
                        lineHeight: "1.5",
                    }}
                >
                    {pollData && pollData.context ? pollData.context : <CircularProgress />}
                </Typography>
            </Section>

            <Section title="Proponents Say:">
                <ArgumentList
                    argumentsList={pollData && pollData.proponent ? pollData.proponent : []}
                    selectedArguments={selectedProponentArguments}
                    setSelectedArguments={setSelectedProponentArguments}
                />
            </Section>

            <Section title="Opponents Say:">
                <ArgumentList
                    argumentsList={pollData && pollData.opponent ? pollData.opponent : []}
                    selectedArguments={selectedOpponentArguments}
                    setSelectedArguments={setSelectedOpponentArguments}
                />
            </Section>

            {candidates.length > 0 && (
                <Section id="reps" title="Your Representatives" backgroundColor= "black" ref={repsRef}>
                    {candidates.map((candidate) => (
                    <Box
                        key={candidate.candidateId}
                        sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                        marginBottom: "16px",
                        padding: "16px",
                        backgroundColor: "black", // Black background
                        borderRadius: "8px", // Rounded corners
                        border: "1px solid white", // White border
                        }}
                      >
                        <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "16px",
                        }}
                        >
                        <Box
                          sx={{
                          width: "64px",
                          height: "64px",
                          borderRadius: "50%",
                          backgroundColor: "rgb(61, 61, 61)",
                          backgroundImage: `url(${candidate.photo_url})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          }}
                        />
                        <Box>
                          <Typography
                          variant="h6"
                          sx={{
                            fontWeight: "bold",
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                          >
                          {candidate.preferredName} {candidate.lastName}
                          <Box
                            sx={{
                            backgroundColor:
                              candidate.officeParties === "Republican"
                              ? "red"
                              : candidate.officeParties === "Democratic"
                              ? "blue"
                              : "purple",
                            color: "white",
                            borderRadius: "4px",
                            padding: "4px 8px",
                            fontSize: "0.875rem",
                            fontWeight: "bold",
                            }}
                          >
                            {candidate.officeParties}
                          </Box>
                          </Typography>
                          <Typography
                          variant="body2"
                          sx={{
                            color: "rgb(161, 161, 170)",
                          }}
                          >
                          {candidate.title}
                          </Typography>
                        </Box>
                        </Box>
                        <Box
                        sx={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                        >
                        {candidate.phone && (
                          <Button
                          variant="contained"
                          sx={{
                            backgroundColor: "black",
                            color: "white",
                            textTransform: "none",
                            "&:hover": {
                            backgroundColor: "rgb(50, 50, 50)",
                            },
                          }}
                          href={`tel:${candidate.phone}`}
                          onClick={() => {
                            setRepClicks([...repClicks, `tel:${candidate.phone}`]);
                            savePollLog();
                          }}
                          >
                          Call
                          </Button>
                        )}
                        {candidate.twitter && (
                          <Button
                          variant="contained"
                          sx={{
                            backgroundColor: "black",
                            color: "white",
                            textTransform: "none",
                            "&:hover": {
                            backgroundColor: "rgb(50, 50, 50)",
                            },
                          }}
                          href={`https://twitter.com/${candidate.twitter}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            setRepClicks([...repClicks, `https://twitter.com/${candidate.twitter}`]);
                            savePollLog();
                          }}
                          >
                          X/Twitter
                          </Button>
                        )}
                        {candidate.facebook && (
                          <Button
                          variant="contained"
                          sx={{
                            backgroundColor: "black",
                            color: "white",
                            textTransform: "none",
                            "&:hover": {
                            backgroundColor: "rgb(50, 50, 50)",
                            },
                          }}
                          href={`https://facebook.com/${candidate.facebook}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => { 
                            setRepClicks([...repClicks, `https://facebook.com/${candidate.facebook}`]);
                            savePollLog();
                          }}
                          >
                          Facebook
                          </Button>
                        )}
                        {candidate.instagram && (
                          <Button
                          variant="contained"
                          sx={{
                            backgroundColor: "black",
                            color: "white",
                            textTransform: "none",
                            "&:hover": {
                            backgroundColor: "rgb(50, 50, 50)",
                            },
                          }}
                          href={`https://instagram.com/${candidate.instagram}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            setRepClicks([...repClicks, `https://instagram.com/${candidate.instagram}`]);
                            savePollLog();
                          }}
                          >
                          Instagram
                          </Button>
                        )}
                        {candidate.website && (
                          <Button
                          variant="contained"
                          sx={{
                            backgroundColor: "black",
                            color: "white",
                            textTransform: "none",
                            "&:hover": {
                            backgroundColor: "rgb(50, 50, 50)",
                            },
                          }}
                          href={candidate.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            setRepClicks([...repClicks, candidate.website]);
                            savePollLog();
                          }}
                          >
                          Website
                          </Button>
                        )}
                        </Box>
                      </Box>
                      ))}
                    </Section>
                  )}

                  {script && (
                    <Section title="Scripts">
                      <Typography
                        dangerouslySetInnerHTML={{ __html: script }}
                      />
                    </Section>
                  )}
                  
                  <Dialog
                    open={dialogOpen}
                    onClose={handleDialogClose}
                    sx={{
                      "& .MuiDialog-paper": {
                      backgroundColor: "black", // Black background for the dialog
                    color: "white", // White text for the dialog
                    borderRadius: "12px", // Slightly more rounded corners
                    padding: "32px", // Increased padding for better spacing
                    maxWidth: "400px", // Match the width of the dialog in the image
                    textAlign: "center", // Center-align content
                    },
                  }}
                  >
                  <DialogTitle sx={{ color: "white", fontWeight: "bold", fontSize: "1.5rem" }}>
                    {dialogLoading ? "Generating scripts..." : "Is this your home ZIP Code?"}
                  </DialogTitle>
                  <DialogContent>
                    {dialogLoading ? (
                    <Box
                      sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100px",
                      }}
                    >
                      <Typography
                      variant="body2"
                      sx={{
                        color: "white",
                        fontSize: "1rem",
                      }}
                      >
                      <CircularProgress />
                      </Typography>
                    </Box>
                    ) : (
                    <>
                      <Typography
                      variant="h4"
                      sx={{
                        marginBottom: "24px",
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "2rem", // Larger font size for the ZIP code
                        }}
                        >
                        {zipCode || <CircularProgress />}
                        </Typography>
                        <Button
                        variant="contained"
                        sx={{
                            backgroundColor: "rgb(250, 250, 250)", // Same button color as the rest of the page
                            color: "black",
                            fontWeight: "bold",
                            fontSize: "1rem", // Match button font size
                            padding: "12px 24px", // Adjust padding for consistency
                            marginBottom: "16px",
                            "&:hover": {
                            backgroundColor: "rgb(229, 231, 235)", // Hover effect for consistency
                            },
                        }}
                        onClick={handleZipSubmit}
                        >
                        Yes, this is correct
                        </Button>
                        <Typography
                        variant="body2"
                        sx={{
                            marginBottom: "16px",
                            textAlign: "center",
                            color: "white",
                            fontSize: "0.875rem", // Match the font size for the separator text
                        }}
                        >
                        - or -
                        </Typography>
                        <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "16px",
                        }}
                        >
                        <TextField
                            label="Enter your ZIP code"
                            variant="outlined"
                            value={zipCode}
                            onChange={(e) => setZipCode(e.target.value)}
                            sx={{
                            "& .MuiInputBase-root": {
                                color: "white", // White text inside the input
                            },
                            "& .MuiInputLabel-root": {
                                color: "rgb(161, 161, 170)", // Label color
                            },
                            "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                borderColor: "rgb(161, 161, 170)", // Border color
                                },
                                "&:hover fieldset": {
                                borderColor: "white", // Hover border color
                                },
                            },
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleZipSubmit}
                            sx={{
                            backgroundColor: "rgb(250, 250, 250)", // Same button color as the rest of the page
                            color: "black",
                            fontWeight: "bold",
                            fontSize: "1rem", // Match button font size
                            padding: "12px 24px", // Adjust padding for consistency
                            "&:hover": {
                                backgroundColor: "rgb(229, 231, 235)", // Hover effect for consistency
                            },
                            }}
                        >
                            Submit
                        </Button>
                        </Box>
                    </>
                    )}
                </DialogContent>
                </Dialog>
                
                <br />
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                        backgroundColor: "white", // White background for this section
                        padding: "16px", // Add padding for spacing
                        borderRadius: "8px", // Rounded corners for consistency
                    }}
                    >
                    <Typography variant="h6" sx={{ mr: 1, fontWeight: "bold", color: "black" }}>
                        Built by
                    </Typography>
                    <Link
                        href="https://www.civiclyenvolved.com"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <img src="/images/logo.svg" alt="Civicly Envolved Logo" style={{ height: "30px" }} />
                    </Link>
                </Box>
        </Box>
    </ThemeProvider>
);
}

export default PollPublicPage;